/**
 * @module cercaliagl/control/ViewInterface
 */
import maplibregl from 'maplibre-gl';
import MapControls from './MapControls';
import Notification, { Severity } from '../Notification';
import { i18n } from '../dictionary';
import Address from './Address';
import InfoMap from './InfoMap';
import Logo from './Logo';
import Clean from './Clean';
import Meteo from './Meteo';
import Zoom from './Zoom';
import GasStations from './GasStations';
import Traffic from './Traffic';
import LayerSwitcher from './LayerSwitcher';
import Isochrones from './Isochrones';
import LogisticRestrictions from './LogisticRestrictions';
import LayerRegions from './LayerRegions';
import Buildings3d from './Buildings3D';
import Draw from './Draw';
import NavBar from './NavBar';
import { isMobile } from '../util';
import FullScreen from './FullScreen';
import ZBE from './ZBE';
import StreetView from './StreetView';
/**
 * @typedef {Object} ViewInterfaceOptions
 * @property {string} target
 * @property {Array.<string>} mapLayers Map base layers
 */
/**
 * @enum {string}
 */
var Positioning = {
    TOP_LEFT: 'top-left',
    TOP_RIGHT: 'top-right',
    BOTTOM_LEFT: 'bottom-left',
    BOTTOM_RIGHT: 'bottom-right',
};
/**
 * @classdesc
 * Hidden control. Only used for internal purposes.
 * @constructor
 * @param {ViewInterfaceOptions} options View interface options
 */
var ViewInterface = /** @class */ (function () {
    /**
     * @param {ViewInterfaceOptions} options
     */
    function ViewInterface(options) {
        /**
         * @private
         * @type {element}
         */
        this.container_ = options.target;
        /**
         * @private
         * @type {Array.<string>}
         */
        this.mapLayers_ = options.mapLayers;
        /**
         * @private
         * @type {import('../Map').default|null}
         */
        this.map_ = null;
        /**
         * @private
         * @type {Clean}
         */
        this.cleanControl_ = null;
        /**
         * @private
         * @type {NavBar}
         */
        this.navBarControl_ = null;
        /**
         * @private
         * @type {MobileBar}
         */
        this.mobileBarControl_ = null;
        /**
         * @private
         * @type {Address}
         */
        this.addressControl_ = null;
        /**
         * @private
         * @type {Meteo}
         */
        this.meteoControl_ = null;
        /**
         * @private
         * @type {GasStations}
         */
        this.gasStationsControl_ = null;
        /**
         * @private
         * @type {LayerSwitcher}
         */
        this.layerSwitcherControl_ = null;
        /**
         * @private
         * @type {Traffic}
         */
        this.trafficControl_ = null;
        /**
         * @private
         * @type {ZBE}
         */
        this.zbeControl_ = null;
        /**
         * @private
         * @type {StreetView}
         */
        this.streetViewControl_ = null;
        /**
         * @private
         * @type {Zoom}
         */
        this.zoomControl_ = null;
        /**
         * @private
         * @type {LayerRegions}
         */
        this.layerRegionsControl_ = null;
        /**
         * @private
         * @type {Buildings3d}
         */
        this.buildings3dControl_ = null;
        /**
         * @private
         * @type {Draw}
         */
        this.drawControl_ = null;
        /**
         * @private
         * @type {HTMLElement}
         */
        this.topContainer_ = null;
        /**
         * @private
         * @type {InfoMap}
         */
        this.infoMapControl_ = null;
        /**
       * @private
       * @type {LogisticRestrictions}
       */
        this.logisticsRestrictionsControl_ = null;
        /**
       * @private
       * @type {FullScreen}
       */
        this.fullScreenControl_ = null;
        /**
         * @private
         * @type {Isochrones}
         */
        this.isochronesControl_ = null;
        /**
         * @private
         * @type {Logo}
         */
        this.logoControl_ = null;
        /**
         * @private
         * @type {Array.<import('../MapStyler').StyleLayerJSON>}
         */
        this.drawStyles_ = options.drawStyles ? options.drawStyles : [];
        /**
         * @private
         * @type {boolean}
         */
        this.adaptDrawStylesToFeatureStyle_ = options.adaptDrawStylesToFeatureStyle || false;
        /**
         * @private
         * @type {boolean}
         */
        this.freehandOnDrawPolygonMobile_ = options.freehandOnDrawPolygonMobile ? options.freehandOnDrawPolygonMobile : false;
        /**
         * @private
         * @type {Array.<Control>}
         */
        this.controls_ = [];
        this.initializeEvents_();
    }
    /**
     * @private
     */
    ViewInterface.prototype.initializeEvents_ = function () {
        var _this = this;
        window.addEventListener('resize', function (event) {
            var parentContainer = isMobile() && event.target.innerWidth <= 480 ? 'bottom-right' : 'top-right';
            var container = _this.container_.querySelector(".mapboxgl-ctrl-".concat(parentContainer));
            if (container) {
                container.append(_this.topContainer_);
            }
        });
    };
    /**
     * @param {import("maplibre-gl").Map} map Map
     */
    ViewInterface.prototype.onAdd = function (map) {
        this.map_ = map.getCercaliaMap();
        this.topContainer_ = document.createElement('div');
        this.topContainer_.className = 'control-interface-container mdc-elevation--z4';
        this.container_.appendChild(this.topContainer_);
        return this.topContainer_;
    };
    ViewInterface.prototype.onRemove = function () {
        this.map_ = undefined;
        this.element_.parentNode.removeChild(this.element_);
    };
    /**
     * @param {Array.<string>|null} options Options
     */
    ViewInterface.prototype.initializeMapControls = function (options, currentBaselayer) {
        // Required controls.
        this.addressControl_ = new Address();
        this.infoMapControl_ = new InfoMap();
        this.logoControl_ = new Logo();
        var drawOptions = {
            styles: this.drawStyles_,
            freehandOnDrawPolygonMobile: this.freehandOnDrawPolygonMobile_,
            adaptDrawStylesToFeatureStyle: this.adaptDrawStylesToFeatureStyle_,
        };
        this.drawControl_ = new Draw(drawOptions);
        this.fullScreenControl_ = new FullScreen();
        // Optional controls.
        this.navBarControl_ = options.indexOf(MapControls.NAVBAR) !== -1 ? new NavBar() : null;
        this.meteoControl_ = options.indexOf(MapControls.METEO) !== -1 ? new Meteo() : null;
        this.zoomControl_ = options.indexOf(MapControls.ZOOM) !== -1 ? new Zoom() : null;
        this.gasStationsControl_ = options.indexOf(MapControls.GAS_STATIONS) !== -1 ? new GasStations() : null;
        this.trafficControl_ = options.indexOf(MapControls.TRAFFIC) !== -1 ? new Traffic() : null;
        this.zbeControl_ = options.indexOf(MapControls.ZBE) !== -1 ? new ZBE() : null;
        this.streetViewControl_ = options.indexOf(MapControls.STREETVIEW) !== -1 ? new StreetView() : null;
        this.layerSwitcherControl_ = options.indexOf(MapControls.LAYER_SWITCHER) !== -1
            ? new LayerSwitcher({ mapLayers: this.mapLayers_, active: currentBaselayer })
            : null;
        this.cleanControl_ = options.indexOf(MapControls.CLEAN) !== -1 ? new Clean() : null;
        this.isochronesControl_ = options.indexOf(MapControls.ISOCHRONES) !== -1 ? new Isochrones() : null;
        this.layerRegionsControl_ = options.indexOf(MapControls.LAYER_REGIONS) !== -1 ? new LayerRegions() : null;
        this.buildings3dControl_ = options.indexOf(MapControls.BUILDINGS3D) !== -1 ? new Buildings3d() : null;
        this.logisticsRestrictionsControl_ = options.indexOf(MapControls.LOGISTICS_RESTRICTIONS) !== -1
            ? new LogisticRestrictions() : null;
        this.addControl(this.addressControl_, Positioning.BOTTOM_LEFT);
        this.addControl(this.infoMapControl_, Positioning.BOTTOM_LEFT);
        this.addControl(this.logoControl_, Positioning.BOTTOM_RIGHT);
        var attrContainer = this.container_.querySelector('.mapboxgl-ctrl-attrib');
        if (attrContainer) {
            var parentAttribt = attrContainer.parentNode;
            var newAttrib = document.createElement('div');
            newAttrib.className = attrContainer.className;
            parentAttribt.appendChild = this.logoControl_.getElement();
            attrContainer.remove();
        }
        if (this.zoomControl_) {
            this.addControl(this.zoomControl_, Positioning.BOTTOM_RIGHT);
            this.zoomControl_.initialize();
        }
        this.addMapboxControl_(new maplibregl.ScaleControl(), Positioning.BOTTOM_LEFT);
        if (this.navBarControl_) {
            this.addControl(this.navBarControl_, Positioning.TOP_LEFT);
            this.navBarControl_.initialize();
        }
        // //Container controls
        if (this.meteoControl_) {
            this.addControl(this.meteoControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.meteoControl_);
        }
        if (this.gasStationsControl_) {
            this.addControl(this.gasStationsControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.gasStationsControl_);
        }
        if (this.trafficControl_) {
            this.addControl(this.trafficControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.trafficControl_);
        }
        if (this.zbeControl_) {
            this.addControl(this.zbeControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.zbeControl_);
        }
        if (this.streetViewControl_) {
            this.addControl(this.streetViewControl_, Positioning.BOTTOM_RIGHT);
            // this.putInContainer(this.streetViewControl_);
        }
        if (this.cleanControl_) {
            this.addControl(this.cleanControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.cleanControl_);
        }
        if (this.logisticsRestrictionsControl_) {
            this.addControl(this.logisticsRestrictionsControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.logisticsRestrictionsControl_);
        }
        if (this.isochronesControl_) {
            this.addControl(this.isochronesControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.isochronesControl_);
        }
        if (this.layerRegionsControl_) {
            this.addControl(this.layerRegionsControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.layerRegionsControl_);
        }
        if (this.buildings3dControl_) {
            this.addControl(this.buildings3dControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.buildings3dControl_);
        }
        if (this.drawControl_) {
            this.addControl(this.drawControl_, Positioning.TOP_RIGHT);
            if (options.indexOf(MapControls.DRAW) !== -1) {
                this.putInContainer(this.drawControl_);
            }
            else {
                this.drawControl_.hide();
            }
            this.drawControl_.initialize();
        }
        if (this.layerSwitcherControl_) {
            this.addControl(this.layerSwitcherControl_, Positioning.TOP_RIGHT);
            this.putInContainer(this.layerSwitcherControl_);
        }
        if (this.fullScreenControl_) {
            this.addControl(this.fullScreenControl_, Positioning.TOP_RIGHT);
            if (options.indexOf(MapControls.FULLSCREEN) !== -1) {
                this.putInContainer(this.fullScreenControl_);
            }
            else {
                this.fullScreenControl_.hide();
            }
            this.putInContainer(this.fullScreenControl_);
        }
    };
    /**
     * @param {import('./Custom').default} control Control
     * @param {number=} [position] Position.
     */
    ViewInterface.prototype.addCustomControl = function (control, position) {
        this.addControl(control);
        this.putInContainer(control, position);
    };
    /**
     * Close all windows and reset navbar.
     */
    ViewInterface.prototype.closeAll = function () {
        if (this.navBarControl_) {
            this.navBarControl_.reset();
        }
        this.closePopups();
    };
    /**
     * Close popups.
     */
    ViewInterface.prototype.closePopups = function () {
        this.addressControl_.closePopup();
        if (this.meteoControl_) {
            this.meteoControl_.closePopup();
        }
        if (this.gasStationsControl_) {
            this.gasStationsControl_.closePopup();
        }
    };
    /**
     * @param {import("./Control").default} control Control.
     * @param {number=} [position] Position.
     */
    ViewInterface.prototype.putInContainer = function (control, position) {
        var element = control.getElement();
        if (position !== undefined) {
            if (position >= this.topContainer_.children.length) {
                this.topContainer_.appendChild(element);
            }
            else {
                this.topContainer_.insertBefore(element, this.topContainer_.children[position]);
            }
        }
        else {
            this.topContainer_.appendChild(element);
        }
    };
    /**
     * @param {Control} control Control.
     * @param {string} position
     */
    ViewInterface.prototype.addMapboxControl_ = function (control, position) {
        var mbMap = this.map_.getMapboxglMap();
        mbMap.addControl(control, position);
    };
    /**
     * @param {Control} control Control.
     * @param {string} [position]
     */
    ViewInterface.prototype.addControl = function (control, position) {
        var name = control.getName();
        if (name && !this.getControlByName(name)) {
            control.setViewInterface(this);
            this.map_.addControl(control, position);
            this.controls_.push(control);
        }
        else {
            var notification = new Notification(i18n('notificationControlAlreadyExists') + name, Severity.ERROR);
            notification.display(this.map_);
        }
    };
    /**
     * Get the map controls.
     * @return {Array.<Control>} Controls.
     */
    ViewInterface.prototype.getControls = function () {
        return this.controls_;
    };
    /**
     * Close and disable controls with dialog. Only one control can be enabled.
     * @param {Control=} [control] Control.
     */
    ViewInterface.prototype.closeDialogs = function (control) {
        this.controls_.forEach(function (_control) {
            var dialog = _control.getDialog();
            if (dialog !== null && _control !== control) {
                control.disable();
            }
        });
    };
    /**
     * @param {string} name Control name.
     * @return {Control|null} Control.
     */
    ViewInterface.prototype.getControlByName = function (name) {
        for (var i = 0; i < this.controls_.length; i += 1) {
            if (this.controls_[i].getName() === name) {
                return this.controls_[i];
            }
        }
        return null;
    };
    /**
     * @return {InfoMap} Info map control
     */
    ViewInterface.prototype.getInfoMapControl = function () {
        return this.infoMapControl_;
    };
    /**
     * @return {Address} Address control.
     */
    ViewInterface.prototype.getAddressControl = function () {
        return this.addressControl_;
    };
    /**
     * @return {NavBar} Address control.
     */
    ViewInterface.prototype.getNavBarControl = function () {
        return this.navBarControl_;
    };
    /**
     * Hide all controls.
     */
    ViewInterface.prototype.hideAll = function () {
        this.setVisibleControls_(false);
    };
    /**
     * Show all controls.
     */
    ViewInterface.prototype.showAll = function () {
        this.setVisibleControls_(true);
    };
    /**
     * Get controls container.
     * @return {HTMLElement} Element.
     */
    ViewInterface.prototype.getTopContainer = function () {
        return this.topContainer_;
    };
    /**
     * @private
     * @param {boolean} visible Visible `true` or `false`.
     */
    ViewInterface.prototype.setVisibleControls_ = function (visible) {
        if (this.zoomControl_) {
            if (visible) {
                this.zoomControl_.show();
            }
            else {
                this.zoomControl_.hide();
            }
        }
        if (this.navBarControl_) {
            if (visible) {
                this.navBarControl_.show();
            }
            else {
                this.navBarControl_.hide();
            }
        }
        this.topContainer_.style.display = visible ? 'flex' : 'none';
    };
    /**
     * @param {import("../layer/XYZ").default|import("../layer/WMS").default} baseLayer Base layer
     */
    ViewInterface.prototype.addCustomLayerToLayerSwitcher = function (baseLayer) {
        if (this.layerSwitcherControl_) {
            this.layerSwitcherControl_.addLayer(baseLayer.getLayerId(), baseLayer.getLabel());
        }
    };
    /**
     * @param {string} id id
     * @param {string} label label
     */
    ViewInterface.prototype.addCustomLayerToLayerSwitcherFromJson = function (id, label) {
        if (this.layerSwitcherControl_) {
            this.layerSwitcherControl_.addLayer(id, label);
        }
    };
    /**
     * Return logo control
     * @return {Logo}
     */
    ViewInterface.prototype.getLogo = function () {
        return this.logoControl_;
    };
    return ViewInterface;
}());
export default ViewInterface;
//# sourceMappingURL=ViewInterface.js.map